import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useCommitmentFileDownload } from 'Hooks/useCommitmentFileDownload';
import { useDispatchApp } from 'Hooks';
import { useAppSelector } from 'Hooks/useAppSelector';
import { FileViewerDialog } from 'Components';
import { IReferralTrackingItem } from 'Interfaces/SelfReferralTracking';
import {
  Download2White,
  LeftArrowWhite,
  SelfServiceReferralsToastFailedIcon,
  SelfServiceReferralsToastRetryIcon,
  SelfServiceReferralsToastSuccessIcon,
} from 'Images';
import { dismissTrackingItem } from 'Store/SelfReferralTrackingSlice';
import { buildReferralTrackingSubtitle } from 'Utils/referralTrackingUtils';
import {
  ISelfReferralToastAction,
  SelfReferralToast,
} from 'Components/SelfReferralToast';
import { useSelfReferralStatusPolling } from './hooks/useSelfReferralStatusPolling';

const TOAST_EXIT_MS = 450;
const TOAST_GAP_MS = 250;
const TOAST_SWITCH_MS = TOAST_EXIT_MS + TOAST_GAP_MS;

export const SelfReferralToastHost = () => {
  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  useSelfReferralStatusPolling();

  const trackingItems = useAppSelector(
    (state) => state.selfReferralTracking.items,
  );
  const dictionary = Dictionary[DICTIONARY.SelfServiceReferrals].tracking;
  const mentalSubjectSuffix =
    Dictionary[DICTIONARY.SelfServiceReferrals].referralCreator.mental
      .subjectSuffix;
  const commitmentFileName =
    Dictionary[DICTIONARY.SelfServiceReferrals].referralCreator
      .commitmentFileName;

  const {
    openCommitmentFromBase64,
    downloadCommitmentByRequestId,
    viewerBase64,
    closeViewer,
  } = useCommitmentFileDownload({
    fileName: commitmentFileName,
  });

  const [visibleTrackingId, setVisibleTrackingId] = useState<string | null>(
    null,
  );
  const [isToastVisible, setIsToastVisible] = useState(false);

  const toastCandidates = useMemo(
    () =>
      trackingItems
        .filter((item) => item.status !== 'pending' && !item.isDismissed)
        .sort((left, right) => left.createdAt - right.createdAt),
    [trackingItems],
  );

  const activeItem = toastCandidates[0] ?? null;

  useEffect(() => {
    if (!activeItem) {
      setIsToastVisible(false);
      setVisibleTrackingId(null);
      return;
    }

    if (visibleTrackingId !== activeItem.trackingId) {
      if (visibleTrackingId) {
        setIsToastVisible(false);
        const timeoutId = window.setTimeout(() => {
          setVisibleTrackingId(activeItem.trackingId);
          setIsToastVisible(true);
        }, TOAST_SWITCH_MS);
        return () => window.clearTimeout(timeoutId);
      }

      setVisibleTrackingId(activeItem.trackingId);
      setIsToastVisible(true);
    }
  }, [activeItem, visibleTrackingId]);

  const dismissItem = useCallback(
    (trackingId: string) => {
      setIsToastVisible(false);
      window.setTimeout(() => {
        dispatch(dismissTrackingItem({ trackingId }));
      }, TOAST_EXIT_MS);
    },
    [dispatch],
  );

  const handleDownload = useCallback(
    async (item: IReferralTrackingItem) => {
      if (item.fileContentBase64) {
        await openCommitmentFromBase64(item.fileContentBase64);
      } else {
        await downloadCommitmentByRequestId(item.requestId);
      }
    },
    [downloadCommitmentByRequestId, openCommitmentFromBase64],
  );

  const getToastContent = (item: IReferralTrackingItem) => {
    const subtitle = buildReferralTrackingSubtitle(
      item.creatorParams,
      mentalSubjectSuffix,
    );

    switch (item.status) {
      case 'ready':
        return {
          statusIconSrc: SelfServiceReferralsToastSuccessIcon,
          title: dictionary.readyTitle,
          subtitle,
          actions: [
            {
              label: dictionary.downloadButton,
              variant: 'contained' as const,
              startIconSrc: Download2White,
              onClick: () => {
                void handleDownload(item);
                dismissItem(item.trackingId);
              },
            },
          ] satisfies ISelfReferralToastAction[],
        };
      case 'failed':
        return {
          statusIconSrc: SelfServiceReferralsToastFailedIcon,
          title: dictionary.failedTitle,
          subtitle,
          actions: [
            {
              label: dictionary.retryButton,
              variant: 'outlined' as const,
              startIconSrc: SelfServiceReferralsToastRetryIcon,
              onClick: () => {
                dismissItem(item.trackingId);
                navigate(ROUTES.SELF_SERVICE_REFERRALS, {
                  state: {
                    referralOutcome: {
                      kind: 'retry',
                      creatorParams: item.creatorParams,
                    },
                  },
                });
              },
            },
            {
              label: dictionary.contactDepartmentButton,
              variant: 'contained' as const,
              endIconSrc: LeftArrowWhite,
              onClick: () => {
                dismissItem(item.trackingId);
                navigate(ROUTES.UNIVERSAL_REQUEST_WITH_PARAMS(1, 1, 77));
              },
            },
          ] satisfies ISelfReferralToastAction[],
        };
      default:
        return null;
    }
  };

  const renderFileViewer = () =>
    viewerBase64 ? (
      <FileViewerDialog
        showDialog
        onClose={closeViewer}
        base64={viewerBase64}
        fileExtension='pdf'
        fileName={commitmentFileName}
      />
    ) : null;

  if (!activeItem) {
    return renderFileViewer();
  }

  const content = getToastContent(activeItem);
  if (!content) {
    return renderFileViewer();
  }

  return (
    <>
      <SelfReferralToast
        key={activeItem.trackingId}
        {...content}
        onClose={() => dismissItem(activeItem.trackingId)}
        isVisible={isToastVisible}
      />
      {renderFileViewer()}
    </>
  );
};
